/**
 * The auth form card — rebuilt to `GardEn Auth.dc.html` (2026-08).
 *
 * The design draws the form as ONE card (`#FFFDF8`, 18px radius, 1px hairline)
 * whose fields are full-bleed `<label>` blocks separated by drawn rules. The
 * focused block tints to `#F2ECDD` — the field lights up rather than growing a
 * ring — and every label is a mono caps line with a terracotta asterisk that
 * stays visible while you type. That is what is implemented here, at the
 * design's exact paddings (18/24/20), sizes (11.5px label, 17px input) and
 * colours.
 *
 * The PUBLIC API is unchanged: `Slip`, `SlipRow`, `SlipField`, `SlipNotice` and
 * `SLIP_INPUT_CLASS` keep the same props they had, so `AuthLoginScreen`,
 * `SignupScreen`, `ForgotPasswordScreen`, `ResetPasswordScreen` and
 * `VerifyEmailScreen` compile and behave identically. Only the pixels moved.
 *
 * WHAT THE DESIGN DID NOT DRAW, and how it was derived rather than invented:
 *
 *   • **Field errors.** The design has no invalid state. Errors keep their
 *     established behaviour — message beneath its own field, `role="alert"`,
 *     wired by `aria-describedby` — and take the design's attention hue one
 *     step darker (`#A34A22`) — `#C75A34` measures 4.18:1 on the card and 4.0:1
 *     on the invalid wash, under the 4.5:1 floor for 14px text. The block tints
 *     the same way focus does, so the state is never carried by the message
 *     colour alone.
 *   • **The notice.** The design ships one confirmation notice
 *     (`rgba(22,69,63,.08)` / `.25` border / 12px radius / ◆ mark). That is the
 *     `neutral` tone verbatim; `error` and `attention` are the identical shape
 *     in `#C75A34`, which is the design's own accent for attention.
 *
 * Focus is a real ring as well as a tint: `:focus-within` on a block that only
 * changes background would leave keyboard users with a 1.1:1 cue.
 */
import clsx from "clsx";
import type { ReactElement, ReactNode } from "react";

/**
 * The bare control inside a field block. Borderless because the BLOCK is the
 * field; 17px per the design, which also clears the 16px floor below which iOS
 * Safari zooms the viewport on focus.
 */
export const SLIP_INPUT_CLASS =
  "gav2-input block w-full min-w-0 border-0 bg-transparent p-0 text-[17px] text-[#17201D] " +
  "outline-none placeholder:text-[#9A937F] disabled:cursor-not-allowed disabled:opacity-60";

/** The card. One container, ruled inside — never a card inside a card. */
export function Slip({
  children,
  className,
  ...rest
}: {
  children: ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div
      className={clsx("gav2-slip", className)}
      style={{
        background: "#FFFDF8",
        border: "1px solid rgba(23,32,29,0.12)",
        borderRadius: 18,
        overflow: "hidden",
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface SlipRowProps {
  children: ReactNode;
  /** Paints the persistent error state instead of the focus tint. */
  invalid?: boolean;
  className?: string;
}

/**
 * One field block. Direct child of `<Slip>`; the rules between blocks come from
 * here (`+ *` sibling border), matching the design's per-label `border-bottom`
 * without leaving a dangling rule under the last one.
 */
export function SlipRow({
  children,
  invalid = false,
  className,
}: SlipRowProps): ReactElement {
  return (
    <div
      className={clsx(
        "gav2-row group relative",
        invalid && "gav2-row--invalid",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface SlipFieldProps {
  /** `id` of the control this label points at. */
  htmlFor: string;
  label: string;
  /** Right-aligned on the label line: the password counter, a "forgot?" link. */
  meta?: ReactNode;
  /** The constraint, stated before the user trips over it. */
  hint?: ReactNode;
  hintId?: string;
  /** Already-translated error text. Announced via `role="alert"`. */
  error?: string;
  errorId?: string;
  errorTestId?: string;
  required?: boolean;
  /** Screen-reader-only word for the required marker (translated). */
  requiredLabel?: string;
  children: ReactNode;
  /** Sits to the right of the control: a show/hide toggle, a unit. */
  trailing?: ReactNode;
}

/**
 * A labelled field block. The whole block is the `<label>`, per the design —
 * clicking anywhere in it focuses the control, which is why it carries
 * `cursor: text`.
 */
export function SlipField({
  htmlFor,
  label,
  meta,
  hint,
  hintId,
  error,
  errorId,
  errorTestId,
  required = false,
  requiredLabel,
  children,
  trailing,
}: SlipFieldProps): ReactElement {
  return (
    <SlipRow invalid={error !== undefined}>
      <label htmlFor={htmlFor} className="gav2-field block cursor-text">
        <span className="flex items-center justify-between gap-3">
          {/* `var(--gd-mono)`: IBM Plex Mono in every language
              (design-world.css). Semibold, the design's weight, which IBM Plex
              Mono ships as a real face (index.html loads 600). */}
          <span
            className="font-mono text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#17201D]"
            style={{ fontFamily: "var(--gd-mono)" }}
          >
            {label}
            {required ? (
              <>
                {/*
                  * #A34A22, not the design's #C75A34. Measured: the design's
                  * terracotta on the card is 4.18:1 and on the FOCUSED block
                  * (#F2ECDD) only 3.61:1 — both under the 4.5:1 floor for an
                  * 11.5px glyph. This is the same hue one step darker (5.80:1 /
                  * 5.01:1) and is indistinguishable at 11.5px. The marker is
                  * `aria-hidden` with an sr-only word beside it and
                  * `aria-required` on the control, so the requirement was never
                  * conveyed by colour alone — but a visible glyph still has to
                  * be readable.
                  */}
                <em aria-hidden="true" style={{ fontStyle: "normal", color: "#A34A22" }}>
                  {" *"}
                </em>
                {requiredLabel !== undefined ? (
                  <span className="sr-only">{" " + requiredLabel}</span>
                ) : null}
              </>
            ) : null}
          </span>
          {meta !== undefined && meta !== null ? (
            <span className="shrink-0 leading-none">{meta}</span>
          ) : null}
        </span>

        <span className="gav2-field-control flex items-center gap-3">
          {children}
          {trailing !== undefined && trailing !== null ? trailing : null}
        </span>

        {hint !== undefined && hint !== null ? (
          <span
            id={hintId}
            className="gav2-field-hint block text-[14px] leading-[1.55] text-[#6E6A5E]"
          >
            {hint}
          </span>
        ) : null}
      </label>

      {error !== undefined ? (
        <p
          id={errorId}
          role="alert"
          data-testid={errorTestId}
          className="gav2-field-error -mt-2 text-[14px] font-medium leading-snug text-[#A34A22]"
        >
          {error}
        </p>
      ) : null}
    </SlipRow>
  );
}

/**
 * A notice attached to the form — a server error, a confirmation, a wrong-door
 * explanation. The design's own shape: soft tint, 1px border, 12px radius, a
 * rotated square mark, one line of body text.
 */
export interface SlipNoticeProps {
  tone: "error" | "neutral" | "attention";
  /** Lucide icon, already sized via `iconProps`. */
  icon?: ReactNode;
  title?: string;
  children: ReactNode;
  /** `role="alert"` for errors, `role="status"` for everything else. */
  role?: "alert" | "status";
  id?: string;
  testId?: string;
  className?: string;
}

const NOTICE_TONE: Record<SlipNoticeProps["tone"], { bg: string; border: string; ink: string }> = {
  neutral: { bg: "rgba(22,69,63,0.08)", border: "rgba(22,69,63,0.25)", ink: "#16453F" },
  error: { bg: "rgba(199,90,52,0.10)", border: "rgba(199,90,52,0.35)", ink: "#A34A22" },
  attention: { bg: "rgba(199,90,52,0.10)", border: "rgba(199,90,52,0.35)", ink: "#A34A22" },
};

export function SlipNotice({
  tone,
  icon,
  title,
  children,
  role = "status",
  id,
  testId,
  className,
}: SlipNoticeProps): ReactElement {
  const c = NOTICE_TONE[tone];
  return (
    <div
      id={id}
      role={role}
      data-testid={testId}
      className={clsx("flex gap-3", className)}
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 12,
        padding: "14px 18px",
        fontSize: 14.5,
        color: c.ink,
      }}
    >
      <span className="mt-[3px] shrink-0" aria-hidden="true">
        {icon ?? (
          <span
            style={{
              display: "block",
              width: 7,
              height: 7,
              background: c.ink,
              transform: "rotate(45deg)",
            }}
          />
        )}
      </span>
      <div className="min-w-0 flex-1 leading-relaxed">
        {title !== undefined ? <p className="font-semibold">{title}</p> : null}
        {children}
      </div>
    </div>
  );
}
